import CampaignIcon from '@mui/icons-material/Campaign';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { AgentProfileData, GHLSearchData } from '../../../types';
import { GHLCreate } from './ghl-create';
import { GHLSubscribe } from './ghl-subscribe';
import { GHLUpdate } from './ghl-update';

export function EmailMarketing(props: {
  AgtNo: string;
  profile: AgentProfileData | null;
}) {
  // Props
  const { AgtNo } = props;
  // State
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [managing, setManaging] = React.useState<'update' | 'subscribe' | null>(
    null,
  );

  // Query - Search in Go High Level
  const path = '/go-high-level/search';
  const queryKey = [path, { AgtNo }];
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const response: {
        data: GHLSearchData | null;
      } = await API.post('ContractingAPI', path, {
        body: { AgtNo },
      });

      return response.data;
    },
  });

  const openMenu = Boolean(anchorEl);
  const handleClickMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Whether or not the user has an account in Go High Level
  const isSubscribed = Boolean(query.data !== null);

  // Whether or not their Go High Level Subscription is Active
  const isActive = Boolean(query.data?.tags.includes('active'));

  return (
    <React.Fragment>
      <Paper elevation={0} sx={{ p: 1, border: '1px solid #00000022' }}>
        <Stack spacing={1} direction="row" alignItems="center">
          <CampaignIcon fontSize="large" sx={{ color: '#b26500' }} />

          <Box>
            <Box sx={{ fontSize: 28, fontWeight: 'bold', lineHeight: 1 }}>
              Email Marketing
            </Box>
            <Box sx={{ fontSize: 14, color: '#9b9b9b' }}>
              {query.isLoading
                ? 'Checking...'
                : query.data?.email || 'with Go High Level'}
            </Box>
          </Box>

          <Box sx={{ flex: 1 }} />

          {query.isLoading ? null : isSubscribed ? (
            <React.Fragment>
              {query.isLoading ? null : isActive ? (
                <Chip
                  avatar={<TaskAltIcon color="inherit" />}
                  label="Active"
                  size="medium"
                  color="success"
                  variant="outlined"
                />
              ) : (
                <Chip
                  avatar={<WarningAmberIcon color="inherit" />}
                  label="Inactive"
                  size="medium"
                  color="warning"
                  variant="outlined"
                />
              )}

              <Box>
                <Tooltip arrow placement="left" title="Manage">
                  <IconButton
                    id="admin-basic-button"
                    aria-controls={openMenu ? 'admin-basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openMenu ? 'true' : undefined}
                    onClick={handleClickMenu}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Tooltip>

                <Menu
                  id="admin-basic-menu"
                  anchorEl={anchorEl}
                  open={openMenu}
                  onClose={handleCloseMenu}
                  MenuListProps={{ 'aria-labelledby': 'admin-basic-button' }}
                >
                  <MenuItem
                    onClick={() => {
                      setManaging('update');
                      handleCloseMenu();
                    }}
                  >
                    Update Email Address
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setManaging('subscribe');
                      handleCloseMenu();
                    }}
                  >
                    Start/Stop Subscription
                  </MenuItem>
                </Menu>
              </Box>
            </React.Fragment>
          ) : (
            <GHLCreate queryKey={queryKey} profile={props.profile} />
          )}
        </Stack>
      </Paper>

      {managing === 'subscribe' ? (
        <GHLSubscribe
          queryKey={queryKey}
          active={isActive}
          onClose={() => setManaging(null)}
        />
      ) : null}

      {managing === 'update' ? (
        <GHLUpdate
          queryKey={queryKey}
          currentEmail={query.data?.email || null}
          onClose={() => setManaging(null)}
        />
      ) : null}
    </React.Fragment>
  );
}
